// firebase.js
import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut } from "firebase/auth";
import { getFirestore, collection, addDoc, getDocs } from "firebase/firestore";
import { getAnalytics } from "firebase/analytics";
import { writeBatch, doc , setDoc,getDoc} from "firebase/firestore";
import { isEmailAuthorized, AUTH_ERRORS } from "./config/auth"; 

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};



// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);
const auth = getAuth(app);
const db = getFirestore(app);



// Google OAuth Login with email authorization
const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, new GoogleAuthProvider());
    const user = result.user;
    
    // Check if the user's email is authorized
    if (!isEmailAuthorized(user.email)) {
      // Sign out unauthorized user immediately
      await signOut(auth);
      throw new Error(`${AUTH_ERRORS.UNAUTHORIZED_EMAIL} (${user.email})`);
    }
    
    // Log successful authentication for authorized user
    await logUserActivity(user.uid, user.displayName, `Authorized login: ${user.email}`);
    return user;
  } catch (error) {
    console.error("Error during Google sign-in: ", error);
    
    // Log failed authentication attempt
    if (error.message.includes('Access denied')) {
      await logError(`Unauthorized access attempt: ${error.message}`, null, 'Unknown');
    } else {
      await logError(`Authentication error: ${error.message}`, null, 'Unknown');
    }
    
    throw error;
  }
};

// Logout
const logOut = () => signOut(auth);

const logUserActivity = async (userId, displayName, activity) => {
  try {
    await addDoc(collection(db, "userActivityLogs"), {
      userId,       // Keep userId for reference
      displayName,  // Add displayName to the log
      activity,
      timestamp: new Date(),
    });
  } catch (error) {
    console.error("Error logging activity: ", error);
  }
};

// Log errors
// Log errors
const logError = async (error, userId = null, displayName = null) => {
  try {
    await addDoc(collection(db, "errorLogs"), {
      error,
      userId,        // Log userId for reference
      displayName,   // Log displayName
      timestamp: new Date(),
    });
  } catch (err) {
    console.error("Error logging error: ", err);
  }
};

// Fetch logs with error handling
const fetchLogs = async (collectionName) => {
  try {
    const querySnapshot = await getDocs(collection(db, collectionName));
    return querySnapshot.docs.map((doc) => doc.data());
  } catch (error) {
    console.error(`Error fetching logs from ${collectionName}: `, error);
    return []; // Return an empty array if there's an error
  }
};
const logFeedbackToFirestore = async (feedbackData) => {
  try {
    await addDoc(collection(db, 'FeedbackLogs'), feedbackData); // Log feedback to Firestore
    console.log("Feedback logged successfully");
  } catch (error) {
    console.error("Error logging feedback:", error);
  }
};


const fetchSessionsWithDetails = async (sessionId) => {
  // Get the session document
  const sessionRef = doc(db, "sessions", sessionId);
  const sessionSnap = await getDoc(sessionRef);
  if (!sessionSnap.exists()) {
    throw new Error("Session not found");
  }
  const sessionData = sessionSnap.data();

  // Now fetch the userJourneys subcollection
  const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
  const journeysSnap = await getDocs(journeysRef);
  // Map each journey document to its data (and ensure images is set, even if empty)
  const userJourneys = journeysSnap.docs.map((doc) => {
    const journey = doc.data();
    return {
      ...journey,
      images: journey.images || [] // ensure images is an array
    };
  });

  return { ...sessionData, userJourneys };
};



// Database for meta learning
// Add image data to Firestore
const saveImageData = async (imageData) => {
  try {
    const docRef = await addDoc(collection(db, 'images'), imageData);
    console.log("Image data saved with ID: ", docRef.id);
    return docRef.id;
  } catch (error) {
    console.error("Error saving image data: ", error);
  }
};

// Update desired outcome for an image
const updateDesiredOutcome = async (imageId, desiredOutcome) => {
  try {
    const imageRef = doc(db, "images", imageId);
    await setDoc(imageRef, { desiredOutcome }, { merge: true });
    console.log("Desired outcome updated successfully.");
  } catch (error) {
    console.error("Error updating desired outcome:", error);
  }
};

// Function to retrieve session data from the "sessions" collection
export const getSessionData = async (sessionId) => {
  const sessionRef = doc(db, "sessions", sessionId);
  const sessionSnap = await getDoc(sessionRef);
  if (sessionSnap.exists()) {
    return sessionSnap.data();
  } else {
    throw new Error("Session not found");
  }
};

// Function to update session data in the "sessions" collection
export const updateSessionData = async (sessionId, sessionData) => {
  const sessionRef = doc(db, "sessions", sessionId);
  // The merge option will update only the provided fields
  await setDoc(sessionRef, sessionData, { merge: true });
};

// Export modules
export { 
  db, 
  auth, 
  signInWithGoogle, 
  logOut, 
  logUserActivity, 
  logError, 
  fetchLogs,
  logFeedbackToFirestore, 
  fetchSessionsWithDetails,
  saveImageData, 
  updateDesiredOutcome,
  isEmailAuthorized 
};